<template>
  <div class="min-h-screen bg-muted/30">
    <div class="container mx-auto px-4 py-12">
      <UiCard class="max-w-2xl mx-auto">
        <UiCardHeader>
          <UiCardTitle>{{ $t('auth.account.title') }}</UiCardTitle>
          <UiCardDescription>{{ $t('auth.account.description') }}</UiCardDescription>
        </UiCardHeader>
        <UiCardContent class="space-y-6">
          <!-- Loading State -->
          <div v-if="isLoading" class="flex justify-center py-8">
            <Icon name="tabler:loader-2" size="48" class="animate-spin text-muted-foreground" />
          </div>

          <!-- User Info -->
          <div v-else-if="session" class="space-y-6">
            <div class="flex items-center gap-4">
              <div class="h-16 w-16 rounded-full bg-muted flex items-center justify-center">
                <Icon name="tabler:user" size="32" class="text-muted-foreground" />
              </div>
              <div>
                <h2 class="text-xl font-semibold">{{ session.user.name }}</h2>
                <p class="text-muted-foreground">{{ session.user.email }}</p>
              </div>
            </div>

            <UiSeparator />

            <!-- Account Actions -->
            <div class="space-y-4">
              <h3 class="text-lg font-medium">{{ $t('auth.account.security') }}</h3>

              <!-- Add Passkey -->
              <div class="flex items-center justify-between rounded-2xl bg-muted/50 p-4">
                <div>
                  <h4 class="font-medium">{{ $t('auth.account.passkeys') }}</h4>
                  <p class="text-sm text-muted-foreground">{{ $t('auth.account.passkeysDescription') }}</p>
                </div>
                <UiButton variant="outline" @click="handleAddPasskey" :disabled="isAddingPasskey">
                  <Icon v-if="isAddingPasskey" name="tabler:loader-2" size="16" class="animate-spin" />
                  <Icon v-else name="tabler:fingerprint" size="16" />
                  {{ $t('auth.account.addPasskey') }}
                </UiButton>
              </div>
            </div>

            <UiSeparator />

            <!-- Content Reports -->
            <div class="space-y-4">
              <h3 class="text-lg font-medium">{{ $t('auth.account.legalSection') }}</h3>
              <NuxtLink :to="$localeRoute('/account/my-reports')">
                <div class="flex items-center justify-between rounded-2xl bg-muted/50 p-4 transition-colors hover:bg-muted/70">
                  <div>
                    <h4 class="font-medium">{{ $t('auth.account.myReports') }}</h4>
                    <p class="text-sm text-muted-foreground">{{ $t('auth.account.myReportsDescription') }}</p>
                  </div>
                  <Icon name="tabler:chevron-right" size="16" class="text-muted-foreground" />
                </div>
              </NuxtLink>
            </div>

            <UiSeparator />

            <!-- Sign Out -->
            <UiButton variant="destructive" @click="handleSignOut" :disabled="isSigningOut" class="w-full">
              <Icon v-if="isSigningOut" name="tabler:loader-2" size="16" class="animate-spin" />
              <Icon v-else name="tabler:logout" size="16" />
              {{ $t('auth.account.signOut') }}
            </UiButton>
          </div>

          <!-- Not Logged In -->
          <div v-else class="text-center py-8">
            <p class="text-muted-foreground mb-4">{{ $t('auth.account.notLoggedIn') }}</p>
            <NuxtLink :to="$localeRoute('/account/login')">
              <UiButton>{{ $t('auth.account.goToLogin') }}</UiButton>
            </NuxtLink>
          </div>
        </UiCardContent>
      </UiCard>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue';
import { definePageMeta, navigateTo, useI18n, useSeoMeta } from '#imports';
import { useAuthClient } from '#composables/useAuthClient';
import { getCachedSession, clearSessionCache } from '#bubba/shared/utils/sessionCache';
import { usePostHog } from '#composables/usePostHog';
import { createLogger } from '#bubba/shared/utils/logger';

const log = createLogger('AccountPage');

definePageMeta({
  middleware: ['auth'],
});

const { $ts, $localeRoute } = useI18n();
useSeoMeta({ title: () => $ts('auth.account.seo.title') });

const authClient = useAuthClient();
const { captureEvent, resetUser } = usePostHog();

const session = ref<any>(null);
const isLoading = ref(true);
const isSigningOut = ref(false);
const isAddingPasskey = ref(false);

onMounted(async () => {
  try {
    const result = await getCachedSession(authClient);
    session.value = result?.data ?? null;
  } catch (e) {
    log.error('Failed to get session', e);
  } finally {
    isLoading.value = false;
  }
});

async function handleSignOut() {
  isSigningOut.value = true;

  try {
    captureEvent('user_logged_out');
    resetUser();
    await authClient.signOut();
    clearSessionCache();
    navigateTo($localeRoute('/'));
  } catch (e) {
    log.error('Sign out failed', e);
  } finally {
    isSigningOut.value = false;
  }
}

async function handleAddPasskey() {
  isAddingPasskey.value = true;

  try {
    await authClient.passkey.addPasskey({
      name: `Passkey - ${new Date().toLocaleDateString()}`,
    });
  } catch (e) {
    log.error('Failed to add passkey', e);
  } finally {
    isAddingPasskey.value = false;
  }
}
</script>
